import * as R from 'ramda'
import { EquipmentGroup, EquipmentGroupStId, Offer, Price } from 'app/types/types'
import { setOfferData } from './current-offer-slice'
import { getOffersPromise, updateOfferPromise } from 'app/app/(client)/db/db'
import { setUnsavedOfferCount } from './unsaved-offer-slice'
import { setOfferEdited, unsavedOffersCount } from 'app/app/(client)/nemo/offer'
import { wrapCreateAsyncThunk } from 'app/app/(client)/utils/async-handler'
import {
  calculateDiscountedPrice,
  applyDiscountToGroupPricesWithRounding,
  getPricesForCurrentOffer
} from 'app/app/(client)/utils/pricing-summary-utils'
import { setContractIntroduction } from 'app/app/(client)/utils/offer'
import { setMBSConfigInvalid } from '../offering/offering-slice'
import { getEscalationBillingPlan } from 'app/services/client/client-api'
import { resetOfferPriceEstimation } from 'app/app/(client)/nemo/offer/price-estimation'
import {
  initializeEscalationFromContract,
  resetEscalationFlow,
  updateEscalatedPrice,
  getContractEquipmentsTotalAnnualValue,
  validateEscalationCommercialTerms,
  calculateFinalizedTotalPrice,
  offerHasSapContract
} from 'app/app/(client)/utils/escalation-offer-utils'

type UpdateOffer = (offer: Offer) => Offer

export const updateOfferAsync = wrapCreateAsyncThunk<void, UpdateOffer>(
  'currentOffer/updateOfferAsync',
  async (updater: UpdateOffer, thunkAPI) => {
    const { dispatch, getState } = thunkAPI
    const {
      currentOffer: { data: currentOfferData },
      offeringDetails: { userProfile },
      configdata: { data: configData }
    } = getState()
    const profile = !R.isEmpty(userProfile) ? userProfile : configData?.profile
    if (!currentOfferData) {
      throw new Error('No current offer available')
    }

    const updatedOffer = updater(currentOfferData)

    const offerWithSubSalesOrg =
      profile && updatedOffer.subSalesOrganization !== profile.subSalesOrganization
        ? { ...updatedOffer, subSalesOrganization: profile.subSalesOrganization }
        : updatedOffer

    const editedOffer = setOfferEdited(offerWithSubSalesOrg)

    dispatch(setOfferData(editedOffer))
    await updateOfferPromise(editedOffer)
    const offersFromIDb = await getOffersPromise()
    const unSavedOffers = unsavedOffersCount(offersFromIDb)
    dispatch(setUnsavedOfferCount(unSavedOffers))
  }
)

export const setOfferAsync = wrapCreateAsyncThunk<void, Offer>(
  'currentOffer/setOfferAsync',
  async (offer: Offer, thunkAPI) => {
    const { dispatch } = thunkAPI
    if (!offer) {
      throw new Error('No offer available')
    }
    dispatch(setOfferData(offer))
    await updateOfferPromise(offer)
    const allOffers = await getOffersPromise()
    const count = unsavedOffersCount(allOffers)
    dispatch(setUnsavedOfferCount(count))
  }
)

// async thunk action to update prices in current offer
export const updateOfferPrices = wrapCreateAsyncThunk<
  void,
  { offer: Offer; discounts?: Record<EquipmentGroupStId, number> }
>('currentOffer/updateOfferPrices', async ({ offer, discounts }, thunkAPI) => {
  const { dispatch, getState } = thunkAPI

  const state = getState()
  if (R.isEmpty(offer) || R.isNil(offer)) {
    return
  }

  const { buildings, equipmentGroups, commercialTerms, opportunity, prices } = offer
  if (!equipmentGroups || !buildings || !commercialTerms) {
    return
  }

  if (offer.isEscalationFlow?.active) {
    await dispatch(updateOfferAsync(_ => offer)).unwrap()
    return
  }

  const { data: configDataState } = state.configdata

  const { frontLineConfig } = configDataState || {}

  if (!frontLineConfig) {
    return
  }

  const { pricing } = frontLineConfig
  if (!pricing) {
    return
  }

  const { variables: pricingVariables } = pricing
  if (!pricingVariables) {
    return
  }

  const effectiveDiscounts: Record<EquipmentGroupStId, number> | undefined =
    discounts ||
    (Array.isArray(prices)
      ? prices.reduce(
        (acc, price: Price) => {
          const { groupId, discount } = price
          if (groupId && typeof discount === 'number') {
            acc[groupId] = discount
          }
          return acc
        },
        {} as Record<EquipmentGroupStId, number>
      )
      : undefined)

  // Calculate updated prices using the utility function
  const { equipmentPrice, isValidPrice } = getPricesForCurrentOffer(
    buildings,
    equipmentGroups,
    commercialTerms,
    opportunity,
    frontLineConfig,
    effectiveDiscounts
  )

  const updatedPrices =
    !discounts && Array.isArray(prices)
      ? equipmentPrice.map(price => {
        const existingEquipmentPrice = prices.find(existingPrice => existingPrice.equipmentId === price.equipmentId)
        const existingDiscount = existingEquipmentPrice?.discount

        if (typeof existingDiscount !== 'number') {
          return price
        }

        return {
          ...price,
          discount: existingDiscount,
          discountedPrice: parseFloat(calculateDiscountedPrice(price.equipmentPrice, existingDiscount).toFixed(2))
        }
      })
      : equipmentPrice

  dispatch(
    updateOfferAsync(_ => resetOfferPriceEstimation({
      ...offer,
      prices: updatedPrices
    }))
  )

  const isValidSelections =
    equipmentGroups &&
    Object.values(equipmentGroups).some(
      (group: EquipmentGroup) =>
        (group?.elevator?.selections?.KCSM_CARE_VISIT && group.elevator.selections.KCSM_CARE_VISIT !== null) ||
        (group?.escalator?.selections?.KCSM_CARE_VISIT && group.escalator.selections.KCSM_CARE_VISIT !== null)
    )

  dispatch(setMBSConfigInvalid(!isValidPrice || !isValidSelections))
})

export const updateOfferGroupDiscounts = wrapCreateAsyncThunk<
  void,
  { discounts: Record<EquipmentGroupStId, number>; groupId?: EquipmentGroupStId }
>('currentOffer/updateOfferGroupDiscounts', async ({ discounts, groupId }, thunkAPI) => {
  const { dispatch } = thunkAPI

  const updatedOfferObj = (currOffer: Offer): Offer => {
    if (!currOffer || !currOffer.prices) {
      return currOffer
    }

    const { prices } = currOffer

    const updatedPricesByEquipmentId = new Map<string, Price>()

    if (groupId) {
      // Update only the specific group
      const groupPrices = prices.filter(price => price.groupId === groupId)
      const groupDiscount = discounts[groupId] ?? 0
      const updatedGroupPrices = applyDiscountToGroupPricesWithRounding(groupPrices, groupDiscount)

      updatedGroupPrices.forEach(price => {
        updatedPricesByEquipmentId.set(price.equipmentId, price)
      })
    } else {
      // Update all groups
      const pricesByGroup = prices.reduce<Record<EquipmentGroupStId, Price[]>>((acc, price) => {
        if (!acc[price.groupId]) {
          acc[price.groupId] = []
        }
        acc[price.groupId].push(price)
        return acc
      }, {})

      Object.entries(pricesByGroup).forEach(([gId, groupPrices]) => {
        const groupDiscount = discounts[gId] ?? 0
        const updatedGroupPrices = applyDiscountToGroupPricesWithRounding(groupPrices, groupDiscount)

        updatedGroupPrices.forEach(price => {
          updatedPricesByEquipmentId.set(price.equipmentId, price)
        })
      })
    }

    const updatedPrices = prices.map(price => updatedPricesByEquipmentId.get(price.equipmentId) || price)

    return resetOfferPriceEstimation({
      ...currOffer,
      prices: updatedPrices
    })
  }

  dispatch(updateOfferAsync(offer => updatedOfferObj(offer)))
})

export const updateOfferTenderInstruction = wrapCreateAsyncThunk<void, string>(
  'currentOffer/updateOfferTenderInstruction',
  async (tenderInstruction, thunkAPI) => {
    const { dispatch, getState } = thunkAPI

    const state = getState()

    const { data: configDataState } = state.configdata
    if (R.isEmpty(configDataState) || R.isNil(configDataState) || !configDataState) {
      return
    }

    const { settings } = configDataState
    if (R.isEmpty(settings) || R.isNil(settings) || !settings) {
      return
    }

    dispatch(updateOfferAsync(offer => setContractIntroduction(settings.language, tenderInstruction)(offer)))
  }
)

export const updateOfferAdditionalDetails = wrapCreateAsyncThunk<void, string>(
  'currentOffer/updateOfferAdditionalDetails',
  async (additionalDetails, thunkAPI) => {
    const { dispatch } = thunkAPI
    dispatch(updateOfferAsync(offer => ({ ...offer, additionalContractDetails: additionalDetails })))
  }
)

export const updateOfferCustomAttachmentComments = wrapCreateAsyncThunk<void, string>(
  'currentOffer/updateOfferCustomAttachmentComments',
  async (customAttachmentComments, thunkAPI) => {
    const { dispatch } = thunkAPI
    dispatch(updateOfferAsync(offer => ({ ...offer, customAttachmentComments })))
  }
)

export const updateOfferAttachments = wrapCreateAsyncThunk<void, number[]>(
  'currentOffer/updateOfferAttachments',
  async (attachmentOption, thunkAPI) => {
    const { dispatch } = thunkAPI
    dispatch(updateOfferAsync(offer => ({ ...offer, attachments: attachmentOption })))
  }
)

export const updateOfferCustomAttachmentSelection = wrapCreateAsyncThunk<void, string[]>(
  'currentOffer/updateOfferCustomAttachmentSelection',
  async (customAttachmentSelection, thunkAPI) => {
    const { dispatch } = thunkAPI
    dispatch(updateOfferAsync(offer => ({ ...offer, customAttachmentSelection })))
  }
)

export const updateWarrantyOptions = wrapCreateAsyncThunk<void, number | undefined>(
  'currentOffer/updateOfferWarranty',
  async (warranty, thunkAPI) => {
    const { dispatch } = thunkAPI
    dispatch(updateOfferAsync(offer => ({ ...offer, warranty })))
  }
)

// Fetch escalation billing plan from Salesforce
export const fetchEscalationBillingPlanAsync = wrapCreateAsyncThunk<void, string>(
  'currentOffer/fetchEscalationBillingPlan',
  async (opportunityId, thunkAPI) => {
    const { dispatch, getState } = thunkAPI
    const state = getState()
    const { data: currentOffer } = state.currentOffer

    if (!currentOffer) {
      throw new Error('No current offer available')
    }

    try {
      // Use centralized API service function
      const billingPlanList = await getEscalationBillingPlan(opportunityId)

      // Auto-populate KCSM_BILLING_PLAN commercial term with the first billing plan
      if (Array.isArray(billingPlanList) && billingPlanList.length > 0) {
        const topBillingPlan = billingPlanList[0]

        if (topBillingPlan && topBillingPlan.billingPlan) {
          dispatch(updateOfferAsync(offer => ({
            ...offer,
            commercialTerms: {
              ...offer.commercialTerms,
              KCSM_BILLING_PLAN: topBillingPlan.billingPlan
            }
          })))
        }
      }
    } catch (error) {
      // Log error but don't fail the initialization
      // User can still manually select billing plan
      console.error('Failed to fetch billing plan:', error)
    }
  }
)

// Escalation flow async thunks
export const initializeContractRenewal = wrapCreateAsyncThunk<void, void>(
  'currentOffer/initializeContractRenewal',
  async (_, thunkAPI) => {
    const { dispatch, getState } = thunkAPI
    const state = getState()
    const { data: currentOffer } = state.currentOffer
    const { data: configData } = state.configdata

    if (!currentOffer || !configData?.frontLineConfig) {
      throw new Error('Missing offer or config data')
    }

    const escalationData = initializeEscalationFromContract(currentOffer, configData.frontLineConfig)

    dispatch(updateOfferAsync(offer => ({
      ...offer,
      ...escalationData
    })))
  }
)

export const updateEscalatedPriceAsync = wrapCreateAsyncThunk<void, number>(
  'currentOffer/updateEscalatedPrice',
  async (newPrice, thunkAPI) => {
    const { dispatch, getState } = thunkAPI
    const state = getState()
    const { data: currentOffer } = state.currentOffer
    const { data: configData } = state.configdata

    if (!currentOffer) {
      throw new Error('No current offer available')
    }

    // Validate price is not lower than original
    const annualValue = getContractEquipmentsTotalAnnualValue(currentOffer)
    if (newPrice < annualValue) {
      throw new Error('Price cannot be lower than original contract value')
    }

    const optionC = configData?.frontLineConfig?.renegotiation_refresh?.find(model => model.renegoModel === 'optionC')
    const currentEscalationMultiplier = optionC?.escalationMultiplier || 10

    const escalationData = updateEscalatedPrice(currentOffer, newPrice, currentEscalationMultiplier)

    // Await the update to ensure state is properly synchronized before continuing
    await dispatch(updateOfferAsync(offer => ({
      ...offer,
      ...escalationData
    }))).unwrap()
  }
)

export const resetEscalationFlowAsync = wrapCreateAsyncThunk<Offer, void>(
  'currentOffer/resetEscalationFlow',
  async (_unused, thunkAPI) => {
    const { dispatch, getState } = thunkAPI
    const state = getState()
    const { data: currentOffer } = state.currentOffer

    if (!currentOffer) {
      throw new Error('No current offer available')
    }

    const resetData = resetEscalationFlow(currentOffer)

    const updatedOffer = {
      ...currentOffer,
      ...resetData
    }

    // Await the update to ensure state is properly synchronized before continuing
    await dispatch(updateOfferAsync(_ => updatedOffer)).unwrap()

    // Return the updated offer so caller can use it
    return updatedOffer
  }
)

// Set default escalation date if missing
export function setDefaultEscalationDate(
  offer: Offer,
  frontlineConfig: any
): Offer {
  const { commercialTerms } = offer
  const contract = R.head(offer.opportunity.contracts || [])

  if (!commercialTerms.KCSM_BASE_MONTH_ESCALATION && contract) {
    const startDate = contract.start
    // Use same logic as old app: frontlineConfig.sap.escalationDate
    // Option '1' (default): Start of next year
    // Option '2': Add 1 year to contract start date
    const escalationDateOption = frontlineConfig?.sap?.escalationDate || '1'

    const contractStartDate = new Date(startDate)
    let escalationDate: Date

    if (escalationDateOption === '1') {
      // Start of next year
      escalationDate = new Date(contractStartDate.getFullYear() + 1, 0, 1)
    } else {
      // Add 1 year to contract start date
      escalationDate = new Date(contractStartDate)
      escalationDate.setFullYear(escalationDate.getFullYear() + 1)
    }

    return {
      ...offer,
      commercialTerms: {
        ...commercialTerms,
        KCSM_BASE_MONTH_ESCALATION: escalationDate.toISOString().split('T')[0]
      }
    }
  }

  return offer
}

// Finalize tender for escalation - complete the tender creation process
export const finalizeTenderAsync = wrapCreateAsyncThunk<void, { comment: string }>(
  'currentOffer/finalizeTender',
  async ({ comment }, thunkAPI) => {
    const { dispatch, getState } = thunkAPI
    const state = getState()
    const { data: currentOffer } = state.currentOffer
    const { data: configData } = state.configdata

    if (!currentOffer) {
      throw new Error('No current offer available')
    }

    if (!configData?.frontLineConfig) {
      throw new Error('No frontline config available')
    }

    const { completeOfferAndScheduleSyncForIt } = await import('../sync/sync-offer-async')

    // Validate SAP contract doesn't exist
    if (offerHasSapContract(currentOffer)) {
      throw new Error('Offer has already been sent to SAP and cannot be modified')
    }

    // Validate commercial terms
    const validation = validateEscalationCommercialTerms(currentOffer, configData.frontLineConfig)
    if (!validation.isValid) {
      // Error messages are already translated in validateEscalationCommercialTerms
      const errorMessages = Object.values(validation.errors).join(', ')
      throw new Error(`Commercial terms incomplete: ${errorMessages}`)
    }

    // Validate escalation flow is active and has price
    if (!currentOffer.isEscalationFlow?.active) {
      throw new Error('Escalation flow is not active')
    }

    if (!currentOffer.isEscalationFlow.escalatedAnnualPrice || currentOffer.isEscalationFlow.escalatedAnnualPrice <= 0) {
      throw new Error('Escalated price must be greater than zero')
    }

    // Set default escalation date if missing
    const offerWithEscalationDate = setDefaultEscalationDate(currentOffer, configData.frontLineConfig)

    // Recompute VAT-inclusive total from the latest commercial terms - interimTotalPrice holds the
    // pre-VAT escalated price only, so it must not be used as the finalized (incl. VAT) total sent to gendoc
    const { isEscalationFlow } = offerWithEscalationDate
    const finalizedData = {
      isEscalationFlow: {
        ...isEscalationFlow,
        finalizedTotalPrice: calculateFinalizedTotalPrice(
          isEscalationFlow.escalatedAnnualPrice,
          offerWithEscalationDate.commercialTerms
        )
      }
    }

    // Prepare offer for finalization
    const offerToFinalize: Offer = {
      ...offerWithEscalationDate,
      ...finalizedData,
      tenderVersionComment: comment,
      // Note: completed, finalization_started, sfTenderVersionActive, modified_client
      // will be set by completeOfferAndScheduleSyncForIt
      // Note: averageDiscountPercentage, discountApprovalComment, discountApprovalLevels, discountSummary
      // are not in the new app's Offer type - backend will handle discount logic
    }

    // Update local state and schedule sync
    // completeOfferAndScheduleSyncForIt will:
    // 1. Set completed = true, finalization_started = true, sfTenderVersionActive = false
    // 2. Update IndexedDB
    // 3. Schedule sync to backend which will handle the 12-step finalization process
    await completeOfferAndScheduleSyncForIt(
      offerToFinalize,
      dispatch,
      () => {
        // No-op callback after sync starts
      }
    )
  }
)
